// Intent (owner, 1.4.0): onboarding asked "masz auto/dom/kredyt?" and then did
// NOTHING with the answer — the user had to find the Polisy tab himself. This
// step (placed at the END of onboarding, owner decision) closes the loop:
// every ticked situation becomes a concrete prompt with a camera "Skanuj"
// button (mock: onboarding_polisy_wydatki/onboarding_twoje_polisy). The photo
// goes through the GUEST scan endpoint (no account needed, device-capped
// server-side). The read is NOT saved silently — the user first REVIEWS the
// read fields ("oceń polisę", owner correction) and only "Zapisz polisę"
// creates the local policy with the photo attached as its document. Nothing
// ticked -> one general "a masz jakieś polisy?" row. Fully skippable;
// checkmarks only, never an X for gaps.

import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { guestScanPolicyApi, PolicyScanDraft } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ensureNotificationSetup } from '@/notifications/local';
import { usePoliciesStore } from '@/store/policies-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { HouseholdDeclarations, Policy, PolicyType } from '@/types/domain';
import { formatDate, formatPln } from '@/utils/format';

type RowKey = 'car' | 'home' | 'mortgage' | 'life' | 'business' | 'any';

interface Row {
  key: RowKey;
  icon: keyof typeof Ionicons.glyphMap;
  labelKey: string;
  hintKey: string;
  /** Policy types that tick this row off once a scanned policy matches. */
  types: PolicyType[] | null; // null = any policy counts (the generic row)
}

const ROW_DEFS: { key: RowKey; when: (d: HouseholdDeclarations, partner: boolean) => boolean; row: Omit<Row, 'key'> }[] = [
  {
    key: 'car',
    when: (d) => d.hasCar,
    row: { icon: 'car-outline', labelKey: 'onboarding.policyCar', hintKey: 'onboarding.policyCarHint', types: ['CAR_OC', 'CAR_AC'] },
  },
  {
    key: 'home',
    when: (d) => d.ownsHome,
    row: { icon: 'home-outline', labelKey: 'onboarding.policyHome', hintKey: 'onboarding.policyHomeHint', types: ['HOME'] },
  },
  {
    key: 'mortgage',
    when: (d) => d.hasMortgage,
    row: { icon: 'card-outline', labelKey: 'onboarding.policyMortgage', hintKey: 'onboarding.policyMortgageHint', types: ['LIFE', 'HOME', 'OTHER'] },
  },
  {
    key: 'life',
    when: (d, partner) => d.hasKids || partner,
    row: { icon: 'shield-checkmark-outline', labelKey: 'onboarding.policyLife', hintKey: 'onboarding.policyLifeHint', types: ['LIFE', 'NNW', 'HEALTH'] },
  },
  {
    key: 'business',
    when: (d) => d.runsBusiness,
    row: { icon: 'briefcase-outline', labelKey: 'onboarding.policyBusiness', hintKey: 'onboarding.policyBusinessHint', types: ['OTHER'] },
  },
];

const VALID_TYPES: PolicyType[] = ['CAR_OC', 'CAR_AC', 'HOME', 'LIFE', 'NNW', 'TRAVEL', 'HEALTH', 'OTHER'];

interface SavedPolicy {
  type: PolicyType;
  summary: string;
}

/** Onboarding step "Twoje polisy" — situation-driven scan prompts. */
export function OnboardingPolicies({
  decl,
  hasPartner,
}: {
  decl: HouseholdDeclarations;
  hasPartner: boolean;
}) {
  const { t } = useTranslation();
  const addManualPolicy = usePoliciesStore((s) => s.addManualPolicy);
  const addPolicyDocument = usePoliciesStore((s) => s.addPolicyDocument);

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedPolicy[]>([]);
  // "Oceń polisę" (owner): a successful read lands HERE first — the user sees
  // the read fields and only "Zapisz polisę" creates the policy. No silent save.
  const [pendingReview, setPendingReview] = useState<{
    draft: PolicyScanDraft;
    file: { uri: string };
  } | null>(null);

  const matched = ROW_DEFS.filter((d) => d.when(decl, hasPartner)).map((d) => ({ key: d.key, ...d.row }));
  // Nothing ticked -> the general "a masz jakieś polisy?" question instead.
  const rows: Row[] =
    matched.length > 0
      ? matched
      : [{ key: 'any', icon: 'document-text-outline', labelKey: 'onboarding.policyGeneric', hintKey: 'onboarding.policyGenericHint', types: null }];

  const savedForRow = (row: Row): SavedPolicy | undefined =>
    saved.find((p) => row.types === null || row.types.includes(p.type));

  // A complete read (insurer + amount) goes to the review card; a partial one
  // is reported honestly and the user finishes in Polisy later.
  const handleDraft = (draft: PolicyScanDraft, file: { uri: string }) => {
    if (!draft.insurerName || !draft.premiumGr || draft.premiumGr <= 0) {
      setError(t('onboarding.policyScanIncomplete'));
      return;
    }
    setPendingReview({ draft, file });
  };

  // "Zapisz polisę" on the review card — only now the policy really exists.
  const confirmSave = async () => {
    if (!pendingReview) return;
    const { draft, file } = pendingReview;
    const type = VALID_TYPES.includes(draft.policyType as PolicyType) ? (draft.policyType as PolicyType) : 'OTHER';
    const policy = await addManualPolicy({
      type,
      customName: null,
      insurerName: draft.insurerName!,
      policyNumber: draft.policyNumber ?? null,
      premiumGr: draft.premiumGr!,
      endDate: draft.endDate ?? null,
      source: 'SCAN',
      subject: draft.subject ?? null,
      startDate: draft.startDate ?? null,
      premiumPeriod:
        draft.premiumPeriod && ['ANNUAL', 'SEMI_ANNUAL', 'QUARTERLY', 'MONTHLY', 'ONE_OFF'].includes(draft.premiumPeriod)
          ? (draft.premiumPeriod as Policy['premiumPeriod'])
          : undefined,
    });
    // The scan IS the policy document — same rule as policy/new.tsx.
    await addPolicyDocument(policy.id, file.uri).catch(() => undefined);
    const summary = [
      draft.insurerName,
      formatPln(draft.premiumGr!),
      draft.endDate ? t('onboarding.policySavedUntil', { date: formatDate(draft.endDate) }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const isFirst = saved.length === 0;
    setSaved((s) => [...s, { type, summary }]);
    setPendingReview(null);
    if (isFirst) {
      // Contextual permission ask: we just promised "przypilnujemy terminu" —
      // that promise needs the notification permission (no-op on web).
      void ensureNotificationSetup().catch(() => undefined);
    }
  };

  const sendScan = async (file: { uri: string; name: string; mimeType: string }) => {
    setScanning(true);
    setError(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('scan-timeout')), 75000);
    });
    let result: Awaited<ReturnType<typeof guestScanPolicyApi>>;
    try {
      result = await Promise.race([guestScanPolicyApi(file), timeout]);
    } catch (e) {
      setScanning(false);
      setError((e as Error)?.message === 'scan-timeout' ? t('newPolicy.scanTimeout') : t('newPolicy.scanError'));
      return;
    } finally {
      if (timer) clearTimeout(timer);
    }
    setScanning(false);
    if (!result.ok || !result.data) {
      // Surface the transport's real reason (offline / guest cap reached / AI down).
      setError(result.message ?? t('newPolicy.scanError'));
      return;
    }
    handleDraft(result.data, file);
  };

  const pickAndScan = async () => {
    setError(null);
    if (Platform.OS !== 'web') {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) return;
    }
    const picked =
      Platform.OS !== 'web'
        ? await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 })
        : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
    if (picked.canceled || !picked.assets?.length) return;
    const asset = picked.assets[0];
    await sendScan({
      uri: asset.uri,
      name: asset.fileName ?? 'polisa.jpg',
      mimeType: asset.mimeType ?? 'image/jpeg',
    });
  };

  const pickPdfAndScan = async () => {
    setError(null);
    const picked = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: true,
      multiple: false,
    });
    if (picked.canceled || !picked.assets?.length) return;
    const asset = picked.assets[0];
    await sendScan({
      uri: asset.uri,
      name: asset.name ?? 'polisa.pdf',
      mimeType: asset.mimeType ?? 'application/pdf',
    });
  };

  return (
    <View style={{ gap: spacing.md }}>
      <AppText variant="headlineMd">{t('onboarding.policiesTitle')}</AppText>
      <AppText variant="bodyMd">
        {matched.length > 0 ? t('onboarding.policiesSubtitle') : t('onboarding.policiesEmptyQuestion')}
      </AppText>
      <Card style={{ padding: 0 }}>
        {rows.map((row, idx) => {
          const savedPolicy = savedForRow(row);
          return (
            <View
              key={row.key}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing.sm,
                padding: spacing.md,
                minHeight: 72,
                borderTopWidth: idx === 0 ? 0 : 1,
                borderTopColor: colors.divider,
              }}
            >
              {/* Icon in a light-blue circle tile — mock onboarding_twoje_polisy. */}
              <View
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: radius.full,
                  backgroundColor: colors.primaryTile,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name={row.icon} size={22} color={colors.navy} />
              </View>
              <View style={{ flex: 1, gap: 2 }}>
                <AppText variant="bodyLg">{t(row.labelKey)}</AppText>
                <AppText variant="labelMd" color={colors.textSecondary}>
                  {savedPolicy ? t('onboarding.policySaved', { summary: savedPolicy.summary }) : t(row.hintKey)}
                </AppText>
              </View>
              {savedPolicy ? (
                <Ionicons name="checkmark-circle" size={26} color={colors.success} />
              ) : (
                // Camera pill (mock): filled navy, icon + "Skanuj".
                <Pressable
                  accessibilityRole="button"
                  disabled={scanning || pendingReview !== null}
                  onPress={() => void pickAndScan()}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.xs,
                    backgroundColor: scanning || pendingReview ? colors.disabledBg : colors.navy,
                    borderRadius: radius.full,
                    paddingHorizontal: spacing.md,
                    minHeight: 44,
                  }}
                >
                  <Ionicons name="camera-outline" size={18} color="#FFFFFF" />
                  <AppText variant="labelLg" color="#FFFFFF">
                    {t('onboarding.policyScanCta')}
                  </AppText>
                </Pressable>
              )}
            </View>
          );
        })}
      </Card>

      {/* "Oceń polisę" — review of the read fields before anything is saved. */}
      {pendingReview && !scanning && (
        <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <Ionicons name="document-text-outline" size={20} color={colors.primary} />
            <AppText variant="headlineSm" style={{ flex: 1 }}>
              {t('onboarding.policyReviewTitle')}
            </AppText>
          </View>
          {[
            { labelKey: 'newPolicy.insurer', value: pendingReview.draft.insurerName },
            { labelKey: 'onboarding.policyReviewPremium', value: formatPln(pendingReview.draft.premiumGr ?? 0) },
            {
              labelKey: 'newPolicy.endDate',
              value: pendingReview.draft.endDate
                ? formatDate(pendingReview.draft.endDate)
                : t('onboarding.policyReviewOpenEnded'),
            },
            { labelKey: 'onboarding.policyReviewNumber', value: pendingReview.draft.policyNumber },
          ].map((f) => (
            <View key={f.labelKey} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <AppText variant="labelMd" color={colors.textSecondary} style={{ width: 120 }}>
                {t(f.labelKey)}
              </AppText>
              <AppText variant="bodyLg" style={{ flex: 1 }}>
                {f.value ?? '—'}
              </AppText>
            </View>
          ))}
          {pendingReview.draft.warning && (
            <AppText variant="labelMd" color={colors.warning}>
              {pendingReview.draft.warning}
            </AppText>
          )}
          <AppText variant="labelMd" color={colors.textSecondary}>
            {t('onboarding.policyReviewHint')}
          </AppText>
          <View style={{ flexDirection: 'row', gap: spacing.sm }}>
            <PrimaryButton
              label={t('onboarding.policyReviewDiscard')}
              variant="secondary"
              onPress={() => setPendingReview(null)}
              style={{ flex: 1 }}
            />
            <PrimaryButton label={t('newPolicy.save')} onPress={() => void confirmSave()} style={{ flex: 1 }} />
          </View>
        </Card>
      )}

      {scanning && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
          <ActivityIndicator color={colors.primary} />
          <View style={{ flex: 1 }}>
            <AppText variant="bodyLg">{t('newPolicy.scanReading')}</AppText>
            <AppText variant="labelMd" color={colors.textSecondary}>
              {t('newPolicy.scanReadingHint')}
            </AppText>
          </View>
        </View>
      )}

      {!scanning && !pendingReview && (
        <PrimaryButton label={t('newPolicy.scanPdf')} variant="secondary" onPress={() => void pickPdfAndScan()} />
      )}

      {saved.length > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
          <Ionicons name="notifications-outline" size={16} color={colors.navy} style={{ marginTop: 2 }} />
          <AppText variant="labelMd" color={colors.navy} style={{ flex: 1 }}>
            {t('onboarding.policiesWatchNote')}
          </AppText>
        </View>
      )}

      {error && !scanning && (
        <AppText variant="labelMd" color={colors.error}>
          {error}
        </AppText>
      )}

      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
        <Ionicons name="information-circle-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
        <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
          {t('onboarding.policiesPrivacyNote')}
        </AppText>
      </View>
    </View>
  );
}
